import { isCountryAddressPart } from './addressDisplay'
import { resolveRoute } from './routing'
import type { RouteProvider } from './routing'
import { parseRouteData } from './routeData'
import type { RoutePoint } from './routeData'
import type { Shipment } from './api'

export type RouteMarker = {
  id: string
  coordinates: [number, number]
  type: string
  label: string
}

export type RouteDiagnostics = {
  totalPointCount: number
  validPointCount: number
  invalidPointCount: number
}

export const TRUCK_ROUTE_ESTIMATE_TOOLTIP = 'Примерная оценка для грузового транспорта: средняя скорость 60 км/ч, с учётом дневного лимита движения. Фактическое время зависит от дороги, стоянок, погоды и режима водителя.'

export type TruckRouteEstimate = {
  drivingHours: number
  drivingHoursLabel: string
  restTimeLabel: string
  fullLabel: string
}

const AVERAGE_TRUCK_SPEED_KMH = 60
const MAX_DRIVING_HOURS_PER_DAY = 9

function getDayLabel(count: number, useSutki = false) {
  if (useSutki) return 'суток'
  if (count % 10 === 1 && count % 100 !== 11) return 'день'
  if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14)) return 'дня'
  return 'дней'
}

export function calculateTruckRouteEstimate(distanceKm: number): TruckRouteEstimate | null {
  if (!Number.isFinite(distanceKm) || distanceKm <= 0) return null

  const drivingHours = Math.max(1, Math.round(distanceKm / AVERAGE_TRUCK_SPEED_KMH))
  const drivingDays = drivingHours / MAX_DRIVING_HOURS_PER_DAY
  const minDays = Math.max(1, Math.floor(drivingDays))
  const maxDays = Math.max(1, Math.ceil(drivingDays))

  const restTimeLabel = minDays >= 3 && minDays !== maxDays
    ? `≈${minDays}–${maxDays} ${getDayLabel(maxDays, true)} с отдыхом`
    : `≈${maxDays} ${getDayLabel(maxDays)} с отдыхом`
  const drivingHoursLabel = `≈${drivingHours} ч движения`

  return {
    drivingHours,
    drivingHoursLabel,
    restTimeLabel,
    fullLabel: `${drivingHoursLabel} · ${restTimeLabel}`,
  }
}

export function isUsableMapCoordinate(coordinates: unknown): coordinates is [number, number] {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return false

  const [lat, lng] = coordinates
  if (typeof lat !== 'number' || typeof lng !== 'number') return false
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false

  // Координаты около 0,0 обычно означают битый или пустой GPS-трек.
  if (Math.abs(lat) < 0.5 && Math.abs(lng) < 0.5) return false

  return true
}

/** Filters a RoutePoint array to only those with valid coordinates. */
export function filterValidCoordinates(points: RoutePoint[]): [number, number][] {
  return points.reduce<[number, number][]>((acc, p) => {
    if (isUsableMapCoordinate(p.coordinates)) acc.push(p.coordinates)
    return acc
  }, [])
}

/** Returns true if json contains at least one point with a usable coordinate. */
export function hasValidRoutePoints(json: unknown): boolean {
  const pts = parseRouteData(json)?.points ?? []
  return pts.some(p => isUsableMapCoordinate(p.coordinates))
}

/** Builds a marker array with loading/intermediate/unloading labels. */
export function buildRouteMarkers(coords: [number, number][]): RouteMarker[] {
  return coords.map((coord, index) => {
    let label = `📍 Точка ${index}`
    let type = 'intermediate'
    if (index === 0) { label = '📦 Погрузка'; type = 'loading' }
    else if (index === coords.length - 1) { label = '🏁 Выгрузка'; type = 'unloading' }
    return { id: `route-${index}`, coordinates: coord, label, type }
  })
}

function normalizeRoutePart(value: string) {
  return value
    .trim()
    .replace(/^(?:г(?:ород)?\.?|город)\s+/i, '')
    .replace(/^(?:пгт\.?|пос[её]лок|пос\.?|село|с\.?|деревня|станица|хутор|аул)\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function isCountryRoutePart(value: string) {
  return isCountryAddressPart(value)
}

function isAdministrativeRoutePart(value: string) {
  return /(?:область|край|республика|автономн|федеральн|округ|район)/i.test(value)
}

function isAddressDetailRoutePart(value: string) {
  return /^(?:ул\.?|улица|пр-?т\.?|проспект|пер\.?|переулок|наб\.?|набережная|ш\.?|шоссе|б-р\.?|бульвар|пл\.?|площадь|д\.?|дом|корп\.?|кв\.?)/i.test(value)
    || /^\d+[\d\s/-]*$/u.test(value)
}

/** A short city/town label for shipment interface copy. */
export function getShortRoutePointLabel(address: string): string {
  const parts = address
    .split(',')
    .map((part) => ({ raw: part.trim(), label: normalizeRoutePart(part) }))
    .filter((part) => part.label && !isCountryRoutePart(part.label))

  if (parts.length === 0) return address.trim()

  const explicitLocality = parts.find(
    (part) => part.raw !== part.label && !isAddressDetailRoutePart(part.raw),
  )
  if (explicitLocality) return explicitLocality.label

  const locality = parts.find(
    (part) => !isAdministrativeRoutePart(part.label) && !isAddressDetailRoutePart(part.raw),
  )
  return locality?.label || parts[0]?.label || address.trim()
}

/** A readable route for interface copy: every waypoint is reduced to its locality. */
export function getRouteDisplayLabel(routeString: string): string {
  if (!routeString) return ''

  return routeString
    .split(/\s*→\s*/)
    .map((part) => getShortRoutePointLabel(part))
    .filter(Boolean)
    .join(' → ')
}

export function extractCitiesFromRoute(routeString: string, truncate = false): string[] {
  if (!routeString) return []

  return routeString
    .split(/\s*→\s*/)
    .map((part) => getShortRoutePointLabel(part))
    .filter(Boolean)
    .map((city) => truncate && city.length > 15 ? `${city.substring(0, 12)}...` : city)
}

// getRealRoute resolves the road geometry for the waypoints. `provider` tells the caller
// whether a real router produced the polyline or it degraded to a straight line between
// the points — the UI should say so instead of presenting a straight line as a road.
export async function getRealRoute(points: [number, number][]): Promise<{
  polyline: [number, number][]
  provider: RouteProvider
}> {
  if (points.length < 2) return { polyline: [], provider: 'straight' }

  try {
    const result = await resolveRoute(points)
    return result.polyline.length > 0
      ? { polyline: result.polyline, provider: result.provider }
      : { polyline: points, provider: 'straight' }
  } catch {
    return { polyline: points, provider: 'straight' }
  }
}

export async function extractRoutePoints(shipment: Shipment): Promise<{
  markers: RouteMarker[]
  polylinePoints: [number, number][]
  diagnostics: RouteDiagnostics
  // null when there weren't enough valid points to route at all.
  provider: RouteProvider | null
}> {
  const markers: RouteMarker[] = []
  let polylinePoints: [number, number][] = []
  let provider: RouteProvider | null = null
  const diagnostics: RouteDiagnostics = {
    totalPointCount: 0,
    validPointCount: 0,
    invalidPointCount: 0,
  }

  const routeData = parseRouteData(shipment.route_json)
  if (routeData?.points && Array.isArray(routeData.points)) {
    const routeCoordinates: [number, number][] = []
    diagnostics.totalPointCount = routeData.points.length

    routeData.points.forEach((point, index) => {
      if (isUsableMapCoordinate(point.coordinates)) {
        const [lat, lng] = point.coordinates
        diagnostics.validPointCount += 1

        let label = ''
        let markerType = 'route-point'

        if (index === 0) {
          label = '📦 Погрузка'
          markerType = 'loading'
        } else if (index === routeData.points!.length - 1) {
          label = '🏁 Выгрузка'
          markerType = 'unloading'
        } else {
          label = `📍 Точка ${index}`
          markerType = 'intermediate'
        }

        markers.push({ id: `route-${index}`, coordinates: [lat, lng], label, type: markerType })
        routeCoordinates.push([lat, lng])
      } else {
        diagnostics.invalidPointCount += 1
      }
    })

    if (routeCoordinates.length >= 2) {
      const real = await getRealRoute(routeCoordinates)
      polylinePoints = real.polyline
      provider = real.provider
    } else {
      polylinePoints = routeCoordinates
    }
  }

  return { markers, polylinePoints, diagnostics, provider }
}

export function calculateMapBounds(
  markers: RouteMarker[],
  polyline: [number, number][]
): { center: [number, number]; zoom: number } {
  const allPoints = [
    ...markers.map(m => m.coordinates),
    ...polyline,
  ].filter(point => point && point.length === 2)

  if (allPoints.length === 0) {
    return { center: [55.751244, 37.618423], zoom: 10 }
  }

  if (allPoints.length === 1) {
    return { center: allPoints[0] as [number, number], zoom: 14 }
  }

  let minLat = Infinity, maxLat = -Infinity
  let minLng = Infinity, maxLng = -Infinity

  allPoints.forEach(([lat, lng]) => {
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
  })

  const centerLat = (minLat + maxLat) / 2
  const centerLng = (minLng + maxLng) / 2
  const latDiff = maxLat - minLat
  const lngDiff = maxLng - minLng
  const zoom = Math.max(5, Math.max(1, Math.min(18, Math.floor(8 - Math.log2(Math.max(latDiff, lngDiff) * 111)))))

  return { center: [centerLat, centerLng], zoom }
}

export function calculateRouteDistance(points: [number, number][]): number {
  if (points.length < 2) return 0

  let totalDistance = 0
  for (let i = 0; i < points.length - 1; i++) {
    const [lat1, lng1] = points[i]
    const [lat2, lng2] = points[i + 1]

    const R = 6371
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLng = (lng2 - lng1) * Math.PI / 180

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2)

    totalDistance += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  }

  return Math.round(totalDistance)
}
